// src/pages/LearnReactPage.js
import React, { useState } from "react";
import Welcome from "../components/Basics/Welcome";
import Clock from "../components/Basics/Clock";

import "./BasicPage.css";

// CodeMirror for syntax highlighting in code blocks
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { vscodeDark } from "@uiw/codemirror-theme-vscode";

function IntermediatePage() {
  const [Code, setCode] = useState();
  const [name, setName] = useState("Sara");
  const [isToggleOn, setToggle] = useState(true);

  const handleClick = () => {
    setToggle(!isToggleOn);
  };

  const Code2 = `const Toggle = () => {
  const [isToggleOn, setToggle] = useState(true);

  const handleClick = () => {
    setToggle(!isToggleOn);
  };

  return (
    <button onClick={handleClick}>
      {isToggleOn ? 'ON' : 'OFF'}
    </button>
  );
};

export default Toggle;`;

  const Code3 = `class LifecycleDemo extends React.Component {
  componentDidMount() {
    console.log('Component did mount');
  }

  componentDidUpdate() {
    console.log('Component did update');
  }

  componentWillUnmount() {
    console.log('Component will unmount');
  }

  render() {
    return <div>Check the console for lifecycle logs.</div>;
  }
}`;

  const Code4 = `function Greeting(props) {
  const isLoggedIn = props.isLoggedIn;
  if (isLoggedIn) {
    return <h1>Welcome back!</h1>;
  }
  return <h1>Please sign up.</h1>;
}`;

  const Code5 = `function NumberList(props) {
  const numbers = props.numbers;
  const listItems = numbers.map((number) =>
    <li key={number.toString()}>
      {number}
    </li>
  );
  return (
    <ul>{listItems}</ul>
  );
}`;

  const Code6 = `const NameForm = () => {
  const [value, setValue] = useState('');

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  const handleSubmit = (event) => {
    alert('A name was submitted: ' + value);
    event.preventDefault();
  };

  return (
    <form onSubmit={handleSubmit}>
      <label>
        Name:
        <input type="text" value={value} onChange={handleChange} />
      </label>
      <input type="submit" value="Submit" />
    </form>
  );
};`;

  const Code7 = `const numbers = [1, 2, 3, 4, 5];
const listItems = numbers.map((number) =>
  <li key={number.toString()}>
    {number}
  </li>
);`;

  const Code8 = `const users = [
  { id: 1, name: 'John' },
  { id: 2, name: 'Doe' },
  { id: 3, name: 'Jane' }
];
const listItems = users.map((user) =>
  <li key={user.id}>
    {user.name}
  </li>
);`;

  return (
    <div className="BasicApp">
      <div className="BasicPage m-2">
        <h2 className="text-3xl">Intermediate Concepts 📚</h2>
        <hr />
      </div>
      <div className="Notes-Container m-4">
        <h3 className="text-xl">Event Handling ✨</h3>
        <hr className="w-[40%] mb-2" />
        <p>
          React handles events in a similar way to DOM elements but with some
          syntactical differences. Here’s a simple example where clicking a
          button toggles its label between "ON" and "OFF" 🔄.
        </p>
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code2}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <h4 className="m-2">Output:</h4>
        <button
          onClick={handleClick}
          type="button"
          className="w-1/6 m-2 py-2.5 px-5 me-2 mb-2 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-full border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
        >
          {isToggleOn ? "ON" : "OFF"}
        </button>
        <h4 className="m-2 font-bold">Explanation:</h4>
        <p className="m-4">
          The Toggle component consists of the following parts:
          <ul>
            <li>&gt;<b>Constructor:</b> Initializes the component’s state and binds the handleClick method. The state is set to true by default.</li>
            <li>&gt;<b>handleClick:</b> Toggles the state between true and false. This method is called when the button is clicked.</li>
            <li>&gt;<b>Render:</b> Displays a button with text depending on the state. `ON` if the state is true, `OFF` if the state is false.</li>
          </ul>
        </p>

        <h3 className="text-xl">Lifecycle Methods 🕰️</h3>
        <hr className="w-[40%] mb-2" />
        <p>
          Lifecycle methods are special methods in class components that run at
          specific points in a component's lifecycle. Here's a basic example 🕰️:
        </p>
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code3}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <h4 className="m-2 font-bold">Explanation:</h4>
        <p className="m-4">
          The LifecycleDemo component consists of the following parts:
          <ul>
            <li>&gt;<b>componentDidMount:</b> Called once when the component is added to the DOM.</li>
            <li>&gt;<b>componentDidUpdate:</b> Called after the component’s updates are flushed to the DOM.</li>
            <li>&gt;<b>componentWillUnmount:</b> Called right before the component is removed from the DOM.</li>
          </ul>
        </p>

        <h3 className="text-xl">Conditional Rendering 👥</h3>
        <hr className="w-[40%] mb-2" />
        <p>
          Render different components or elements based on a condition. Here’s
          an example of greeting users based on their login status:
        </p>
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code4}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <h4 className="m-2 font-bold">Explanation:</h4>
        <p className="m-4">
          The Greeting component consists of the following parts:
          <ul>
            <li>&gt;<b>Conditional Rendering:</b> Checks `isLoggedIn` prop to determine which message to display.</li>
          </ul>
        </p>

        <h3 className="text-xl">Lists and Keys 🔢</h3>
        <hr className="w-[40%] mb-2" />
        <p>
          Rendering a list of items with unique keys helps React identify which
          items have changed. Here’s a basic example that renders a list of
          numbers:
        </p>
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code5}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <h4 className="m-2 font-bold">Explanation:</h4>
        <p className="m-4">
          The NumberList component consists of the following parts:
          <ul>
            <li>&gt;<b>Key Attribute:</b> Assigns a unique key to each list item to help React identify changes.</li>
          </ul>
        </p>

        <h3 className="text-xl">Forms and Controlled Components 📝</h3>
        <hr className="w-[40%] mb-2" />
        <p>
          Controlled components allow React to control the form elements.
          Here’s an example of a controlled input field:
        </p>
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code6}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <h4 className="m-2 font-bold">Explanation:</h4>
        <p className="m-4">
          The NameForm component consists of the following parts:
          <ul>
            <li>&gt;<b>Input Value:</b> The input value is controlled by React state.</li>
            <li>&gt;<b>onChange:</b> Updates the state when the input value changes.</li>
            <li>&gt;<b>onSubmit:</b> Alerts the input value when the form is submitted.</li>
          </ul>
        </p>

        <h3 className="text-xl">Map Function 📋</h3>
        <hr className="w-[40%] mb-2" />
        <p>
          The `map()` function is used to iterate over an array and render each
          item. Here’s an example that renders a list of items:
        </p>
        <h4 className="m-2">Example (Mapping on Numbers):</h4>
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code7}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <h4 className="m-2">Example (Mapping on Objects in an Array):</h4>
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code8}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <h4 className="m-2 font-bold">Explanation:</h4>
        <p className="m-4">
          The mapping examples consist of the following parts:
          <ul>
            <li>&gt;<b>Mapping:</b> Iterates over each item in the array and returns a list of elements.</li>
          </ul>
        </p>
      </div>
    </div>
  );
}

export default IntermediatePage;
